import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Icon,
  Input,
  SearchInput,
  Segmented,
  StatePanel,
  Toggle,
  useToast,
} from "../components/ui";
import { waBroadcastApi, apiError } from "./waBroadcastApi";
import { useWaAccount } from "./waAccount";
import { SHARE_TYPE_OPTIONS } from "./waBroadcastHelpers";

// WhatsApp answers "still loading" (503) for a while after a number links;
// the page waits and asks again by itself instead of making someone click.
const SYNC_RETRY_MS = 15000;
const SYNC_MAX_TRIES = 20;

const VIEWS = [
  { value: "lists", label: "Lists" },
  { value: "groups", label: "Groups" },
];

/**
 * Who the number posts to. Groups come ONLY from the phone (the number must
 * already be a member); lists are saved selections of those groups, picked
 * once and reused from the Send sheet.
 */
export function WaBroadcastAudience({ connected }) {
  const accountId = useWaAccount();
  const toast = useToast();
  const [view, setView] = useState("lists");
  const [groups, setGroups] = useState(null);
  const [lists, setLists] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [g, l] = await Promise.all([waBroadcastApi.groups(accountId), waBroadcastApi.lists(accountId)]);
      setGroups(g.groups);
      setLists(l.lists);
      setError("");
      if (!l.lists.length) setView("groups");
    } catch (err) {
      setError(apiError(err, "Could not load the groups."));
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // Reading the groups from the phone: on demand, and once by itself the first
  // time a connected number has none (a just-linked number). "Still loading"
  // is not an error — it waits and tries again quietly.
  const [syncing, setSyncing] = useState(false);
  const [syncWaiting, setSyncWaiting] = useState(false);
  const [syncError, setSyncError] = useState("");
  const retry = useRef(null);
  const listsRef = useRef(null);
  listsRef.current = lists ? lists.length : null;
  const autoSynced = useRef(false);
  useEffect(() => () => clearTimeout(retry.current), []);

  const sync = useCallback(async (attempt = 0) => {
    clearTimeout(retry.current);
    setSyncing(true);
    setSyncError("");
    try {
      const fresh = (await waBroadcastApi.syncGroups(accountId)).groups;
      setGroups(fresh);
      if (fresh.length === 0) {
        toast.warning("No groups found on this number", {
          description: "Only groups this WhatsApp number is already a member of can be used. Join them on the phone, then refresh.",
        });
      } else {
        toast.success(`Loaded ${fresh.length} group${fresh.length === 1 ? "" : "s"} from WhatsApp`, {
          description: listsRef.current === 0
            ? "Next: open Saved lists → New list, and tick the groups you want to post to."
            : "Your groups are up to date.",
        });
      }
      setSyncWaiting(false);
      setSyncing(false);
    } catch (err) {
      if (err?.response?.status === 503 && attempt + 1 < SYNC_MAX_TRIES) {
        setSyncWaiting(true);
        retry.current = setTimeout(() => sync(attempt + 1), SYNC_RETRY_MS);
        return;
      }
      setSyncWaiting(false);
      setSyncing(false);
      setSyncError(apiError(err, "Could not read the groups from WhatsApp."));
    }
  }, [accountId, toast]);

  useEffect(() => {
    if (connected && groups && groups.length === 0 && !autoSynced.current) {
      autoSynced.current = true;
      sync();
    }
  }, [connected, groups, sync]);

  const patchGroup = (group) => setGroups((gs) => gs.map((g) => (g.id === group.id ? group : g)));
  const patchList = (list) => setLists((ls) => ls.map((l) => (l.id === list.id ? list : l)));

  return (
    <Card className="wab-card wab-audience" padding="lg">
      <div className="wab-card__head">
        <h2 className="wab-visually-hidden">Lists and groups</h2>
        <Segmented
          label="Show"
          size="sm"
          options={VIEWS.map((v) => {
            const n = v.value === "lists" ? lists?.length : groups?.length;
            return n == null ? v : { ...v, label: `${v.label} · ${n}` };
          })}
          value={view}
          onChange={setView}
        />
      </div>

      {error && <Alert tone="error" title={error} action={{ label: "Retry", onClick: load }} />}

      {groups == null || lists == null ? (
        !error && <StatePanel state="loading" loadingShape="row" loadingCount={3} compact />
      ) : view === "groups" ? (
        <GroupsView
          connected={connected}
          groups={groups}
          onPatch={patchGroup}
          onSync={() => sync()}
          syncing={syncing}
          waiting={syncWaiting}
          error={syncError}
        />
      ) : (
        <ListsView
          groups={groups}
          lists={lists}
          onCreated={(list) => setLists((ls) => [...ls, list])}
          onPatch={patchList}
          onDeleted={(id) => setLists((ls) => ls.filter((l) => l.id !== id))}
          onShowGroups={() => setView("groups")}
        />
      )}
    </Card>
  );
}

// ── Groups ───────────────────────────────────────────────────────────────────

function GroupsView({ connected, groups, onPatch, onSync, syncing, waiting, error }) {
  const [query, setQuery] = useState("");

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? groups.filter((g) => g.name.toLowerCase().includes(q)) : groups;
  }, [groups, query]);

  const allowed = groups.filter((g) => !g.unavailable_reason).length;

  return (
    <div className="wab-groups">
      <div className="wab-toolbar">
        <SearchInput
          size="md"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery("")}
          placeholder="Search groups"
          aria-label="Search groups"
        />
        <Button
          variant="secondary"
          size="md"
          loading={syncing}
          disabled={!connected}
          leadingIcon={<Icon name="RefreshCw" size="sm" />}
          onClick={onSync}
        >
          Refresh from WhatsApp
        </Button>
      </div>

      {waiting && (
        <p className="wab-hint" role="status" aria-live="polite">
          <Icon name="Clock" size="xs" /> WhatsApp is still loading this number's chats. The groups appear here by themselves — no need to click again.
        </p>
      )}
      {error && <Alert tone="error" title={error} />}

      {groups.length === 0 && syncing ? (
        <StatePanel
          state="loading"
          compact
          loadingShape="row"
          loadingCount={3}
          title="Reading your groups from WhatsApp…"
        />
      ) : groups.length === 0 ? (
        <StatePanel
          state="empty"
          compact
          icon="Users"
          title={connected ? "No groups loaded yet" : "Link the number first"}
          description={
            connected
              ? "Refresh to read the groups this number is already in. Nothing is joined or created."
              : "Groups are read from the linked phone — only groups it is already a member of."
          }
        />
      ) : (
        <>
          <p className="wab-hint">
            {allowed} of {groups.length} groups can receive posts. Switch a group off to stop posting there for good.
          </p>
          <ul className="wab-rows">
            {shown.filter((g) => g.can_send !== false).map((group) => (
              <GroupRow key={group.id} group={group} onPatch={onPatch} />
            ))}
          </ul>
          <AdminsOnly groups={shown.filter((g) => g.can_send === false)}>
            {(group) => <GroupRow key={group.id} group={group} onPatch={onPatch} />}
          </AdminsOnly>
          {shown.length === 0 && <p className="wab-hint">No group matches “{query}”.</p>}
        </>
      )}
    </div>
  );
}

function GroupRow({ group, onPatch }) {
  const accountId = useWaAccount();
  const toast = useToast();
  const toggle = async (next) => {
    try {
      onPatch((await waBroadcastApi.updateGroup(group.id, { opted_out: !next }, accountId)).group);
      if (next) {
        toast.success(`Posting to “${group.name}” is on`);
      } else {
        toast.info(`“${group.name}” switched off`, {
          description: "Nothing will be posted there, and anything queued for it is skipped. Switch it back on any time.",
        });
      }
    } catch (err) {
      throw new Error(apiError(err, "Couldn't save — try again"));
    }
  };

  return (
    <li className="wab-row">
      <GroupAvatar group={group} />
      <div className="wab-row__main">
        <span className="wab-row__name">{group.name}</span>
        <span className="wab-row__meta">
          {group.participants} members
          {group.list_ids?.length ? ` · in ${group.list_ids.length} list${group.list_ids.length > 1 ? "s" : ""}` : ""}
        </span>
      </div>
      {!group.can_send ? (
        <Badge tone="danger" size="sm">Admins only</Badge>
      ) : (
        <Toggle
          size="sm"
          tone="active"
          checked={!group.opted_out}
          onChange={toggle}
          aria-label={`Post to ${group.name}`}
        />
      )}
    </li>
  );
}

/**
 * Groups where only admins can post: we can't send there unless this number is
 * an admin (most of the time it isn't), so they sit apart, collapsed, at the
 * bottom (Alex, 2026-10-13). Renders nothing when there are none.
 */
function AdminsOnly({ groups, children }) {
  const [open, setOpen] = useState(false);
  if (!groups.length) return null;
  return (
    <div className={`wab-admins-only ${open ? "is-open" : ""}`}>
      <button type="button" className="wab-admins-only__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="Lock" size="sm" />
        <span>Admins only · {groups.length} group{groups.length === 1 ? "" : "s"}</span>
        <span className="wab-admins-only__why">Only group admins can post here</span>
        <Icon name={open ? "ChevronUp" : "ChevronDown"} size="sm" />
      </button>
      {open && <ul className="wab-checks wab-rows">{groups.map(children)}</ul>}
    </div>
  );
}

/**
 * The group's WhatsApp picture when the phone had it cached, else an icon.
 * WhatsApp's picture links expire, so a broken one falls back quietly; the
 * next "Refresh from WhatsApp" renews them.
 */
function GroupAvatar({ group, size = "md" }) {
  const [broken, setBroken] = useState(false);
  const cls = `wab-row__avatar wab-row__avatar--${size}`;
  if (group.avatar_url && !broken) {
    return <img className={cls} src={group.avatar_url} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  return <span className={cls} aria-hidden="true"><Icon name="Users" size="sm" /></span>;
}

// ── Saved lists ──────────────────────────────────────────────────────────────

function ListsView({ groups, lists, onCreated, onPatch, onDeleted, onShowGroups }) {
  const accountId = useWaAccount();
  const toast = useToast();
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const create = async () => {
    setCreating(true);
    setError("");
    try {
      const { list } = await waBroadcastApi.createList({ name: `List ${lists.length + 1}` }, accountId);
      onCreated(list);
      toast.success("List created", {
        description: "Give it a name and tick the groups to include — every change saves by itself.",
      });
      setOpenId(list.id);
    } catch (err) {
      setError(apiError(err, "Could not create the list."));
    } finally {
      setCreating(false);
    }
  };

  if (groups.length === 0) {
    return (
      <StatePanel
        state="empty"
        compact
        icon="List"
        title="Load the groups first"
        description="Lists are built from the groups the number is already in."
        action={{ label: "Go to groups", onClick: onShowGroups }}
      />
    );
  }

  return (
    <div className="wab-lists">
      {error && <Alert tone="error" title={error} />}

      {lists.length === 0 ? (
        <StatePanel
          state="empty"
          compact
          icon="List"
          title="No saved lists yet"
          description="Save a set of groups once — “UK dealers”, “Rolex buyers” — and sending becomes one tap."
          action={{ label: "New list", onClick: create, loading: creating }}
        />
      ) : (
        <>
          <ul className="wab-rows">
            {lists.map((list) => (
              <ListRow
                key={list.id}
                list={list}
                groups={groups}
                open={openId === list.id}
                onToggle={() => setOpenId(openId === list.id ? null : list.id)}
                onPatch={onPatch}
                onDeleted={onDeleted}
              />
            ))}
          </ul>
          <Button variant="secondary" size="md" loading={creating} leadingIcon={<Icon name="Plus" size="sm" />} onClick={create}>
            New list
          </Button>
        </>
      )}
    </div>
  );
}

function ListRow({ list, groups, open, onToggle, onPatch, onDeleted }) {
  const accountId = useWaAccount();
  const toast = useToast();
  const [query, setQuery] = useState("");
  const [name, setName] = useState(list.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const save = async (patch) => {
    setError("");
    try {
      const { list: saved } = await waBroadcastApi.updateList(list.id, patch, accountId);
      onPatch(saved);
      return saved;
    } catch (err) {
      const message = apiError(err, "Couldn't save — try again");
      setError(message);
      throw new Error(message);
    }
  };

  const toggleGroup = (groupId, on) => {
    const ids = on ? [...new Set([...list.group_ids, groupId])] : list.group_ids.filter((id) => id !== groupId);
    onPatch({ ...list, group_ids: ids }); // instant; the save confirms it
    save({ group_ids: ids }).catch(() => onPatch(list));
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await waBroadcastApi.deleteList(list.id, accountId);
      onDeleted(list.id);
      toast.success(`Deleted “${list.name}”`, { description: "The groups themselves are unchanged." });
    } catch (err) {
      setError(apiError(err, "Could not delete the list."));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const typeLabel = SHARE_TYPE_OPTIONS.find((o) => o.value === (list.share_type || ""))?.label;
  const inList = new Set(list.group_ids);
  // The list's own groups on top (in it first, then the rest), so opening a
  // list shows what it contains; admins-only groups go to their own section.
  const visible = groups
    .filter((g) => g.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => Number(inList.has(b.id)) - Number(inList.has(a.id)));
  const postable = visible.filter((g) => g.can_send !== false || inList.has(g.id));
  const adminsOnly = visible.filter((g) => g.can_send === false && !inList.has(g.id));
  const renderCheck = (g) => (
    <li key={g.id}>
      <Checkbox
        checked={inList.has(g.id)}
        disabled={!!g.unavailable_reason && !inList.has(g.id)}
        onChange={(on) => toggleGroup(g.id, on)}
        label={<span className="wab-check-label"><GroupAvatar group={g} size="sm" />{g.name}</span>}
        description={g.unavailable_reason || `${g.participants} members`}
      />
    </li>
  );

  return (
    <li className={`wab-list ${open ? "is-open" : ""}`}>
      <button type="button" className="wab-list__summary" aria-expanded={open} onClick={onToggle}>
        <span className="wab-row__avatar" aria-hidden="true"><Icon name="List" size="sm" /></span>
        <span className="wab-row__main">
          <span className="wab-row__name">{list.name}</span>
          <span className="wab-row__meta">
            {list.group_ids.length} group{list.group_ids.length === 1 ? "" : "s"}
            {list.share_type ? ` · ${typeLabel}` : ""}
          </span>
        </span>
        {list.is_default && <Badge tone="accent" size="sm">Default</Badge>}
        <Icon name={open ? "ChevronUp" : "ChevronDown"} size="sm" />
      </button>

      {open && (
        <div className="wab-list__body">
          {error && <Alert tone="error" title={error} />}

          <Field label="Name" onSave={(name) => save({ name })} debounce={700}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} fullWidth />
          </Field>

          <div className="wab-list__settings">
            <Segmented
              label="Use for"
              size="sm"
              options={SHARE_TYPE_OPTIONS}
              value={list.share_type || ""}
              onChange={(value) => save({ share_type: value || null }).catch(() => {})}
            />
            <Toggle
              size="sm"
              checked={list.is_default}
              onChange={(next) =>
                save({ is_default: next }).then(() =>
                  toast.success(next ? `“${list.name}” is pre-selected when sending` : `“${list.name}” is no longer pre-selected`)
                )
              }
              label={list.share_type ? `Choose automatically when sharing ${typeLabel.toLowerCase()}` : "Choose automatically when sharing"}
            />
          </div>

          <div className="wab-toolbar">
            <SearchInput
              size="md"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery("")}
              placeholder="Find a group"
              aria-label="Find a group"
            />
          </div>

          {list.group_ids.length > 0 && !query && (
            <p className="wab-hint">{list.group_ids.length} group{list.group_ids.length === 1 ? "" : "s"} in this list, shown first.</p>
          )}
          <ul className="wab-checks">{postable.map(renderCheck)}</ul>
          <AdminsOnly groups={adminsOnly}>{renderCheck}</AdminsOnly>

          <div className="wab-list__foot">
            <Button variant="ghost" size="sm" leadingIcon={<Icon name="Trash2" size="sm" />} onClick={() => setConfirmDelete(true)}>
              Delete list
            </Button>
          </div>

          <ConfirmDialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            onConfirm={remove}
            loading={deleting}
            danger
            title={`Delete “${list.name}”?`}
            description="The groups stay as they are — only this saved selection is removed."
            confirmLabel="Delete list"
          />
        </div>
      )}
    </li>
  );
}
